import React, { useState, useEffect } from 'react';
import { doc, setDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { firestore } from '../lib/firebaseConfig';
import { useAuth } from '../context/AuthContext';
import Modal from './Modal';
import Button from './Button';
import FormInput from './FormInput';
import FormLabel from './FormLabel';
import BlockTitle from './BlockTitle';
import './custom-bootstrap.scss';
import { STATUSY_PLATNOSCI } from '../lib/paymentStatus';

const EditInstallmentModal = ({ isOpen, onClose, investmentId, flatId, installment, flatCode }) => {
  const { isAdmin, isProjectManager, currentUser } = useAuth();
  const [editableInstallment, setEditableInstallment] = useState({ ...installment });
  const [submitting, setSubmitting] = useState(false);
  const [changeNote, setChangeNote] = useState('');

  useEffect(() => {
    setEditableInstallment({ ...installment });
    setChangeNote('');
  }, [installment]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    // Number inputs always yield strings; coerce to a number so amounts are not
    // stored as text (a string here is what produced paidAmount: '69564' and the
    // broken "06956400000000" totals from string concatenation).
    setEditableInstallment((prev) => ({
      ...prev,
      [name]: type === 'number' && value !== '' ? parseFloat(value) : value,
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (isAdmin()) {
        // Admin: direct save
        const docRef = doc(
          firestore,
          `investments/${investmentId}/flats/${flatId}/payments/${installment.id}`
        );
        await setDoc(docRef, editableInstallment, { merge: true });
      } else if (isProjectManager()) {
        // PM: create change request for admin approval
        const changes = {};
        const original = installment;

        // Detect which fields changed
        const trackFields = [
          'paymentDueDate', 'payment_status', 'paidAmount', 'remainingAmount',
          'paymentDates', 'withdrawnAmount', 'dateField', 'dueType', 'linkedStage', 'fixedDueDate'
        ];
        for (const field of trackFields) {
          if (String(editableInstallment[field] ?? '') !== String(original[field] ?? '')) {
            changes[field] = {
              from: original[field] ?? null,
              to: editableInstallment[field] ?? null,
            };
          }
        }

        if (Object.keys(changes).length === 0) {
          onClose();
          return;
        }

        await addDoc(collection(firestore, 'changeRequests'), {
          type: 'payment_edit',
          status: 'pending',
          investmentId,
          flatId,
          flatCode: flatCode || '',
          paymentId: installment.id,
          paymentNumber: installment.paymentNumber,
          changes,
          note: changeNote,
          requestedBy: currentUser?.uid || '',
          requestedByEmail: currentUser?.email || '',
          createdAt: serverTimestamp(),
        });

        alert('Zmiana została wysłana do zatwierdzenia przez administratora.');
      }

      onClose();
    } catch (error) {
      console.error('Error saving payment:', error);
      alert('Wystąpił błąd podczas zapisywania.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-8">
        <BlockTitle className="text-[22px]">
          Edycja płatności
        </BlockTitle>

        {isProjectManager() && (
          <div className="mt-2 mb-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800">
            Zmiany wymagają zatwierdzenia przez administratora.
          </div>
        )}

        <form onSubmit={handleSave} className="grid grid-cols-2 gap-6 mt-6">
          <div>
            <FormLabel htmlFor="dueType">Typ terminu</FormLabel>
            <select
              id="dueType"
              name="dueType"
              value={editableInstallment.dueType || 'stage'}
              onChange={handleChange}
              className="w-full h-[42px] px-3 border border-[#D7D7D7] rounded-[6px] bg-white text-[var(--electric-blue)] focus:outline-none focus:border-[var(--electric-blue)]"
            >
              <option value="stage">Etap budowy</option>
              <option value="fixed_date">Stała data</option>
            </select>
          </div>

          {editableInstallment.dueType === 'fixed_date' ? (
            <div>
              <FormLabel htmlFor="fixedDueDate">Stały termin płatności</FormLabel>
              <FormInput
                id="fixedDueDate"
                name="fixedDueDate"
                type="date"
                value={editableInstallment.fixedDueDate || ''}
                placeholder="Wybierz datę"
                onChange={handleChange}
              />
            </div>
          ) : (
            <div>
              <FormLabel htmlFor="linkedStage">Powiązany etap</FormLabel>
              <FormInput
                id="linkedStage"
                name="linkedStage"
                type="number"
                min="0"
                max="9"
                value={editableInstallment.linkedStage ?? editableInstallment.stageNumber ?? ''}
                placeholder="Nr etapu"
                onChange={(e) => setEditableInstallment(prev => ({ ...prev, linkedStage: parseInt(e.target.value) || 0 }))}
              />
            </div>
          )}

          <div>
            <FormLabel htmlFor="dueDate">Termin z AN</FormLabel>
            <FormInput
              id="dueDate"
              name="dueDate"
              value={editableInstallment.dueDate || ''}
              placeholder="Termin z AN"
              disabled
            />
          </div>
          <div>
            <FormLabel htmlFor="paymentDueDate">Termin płatności</FormLabel>
            <FormInput
              id="paymentDueDate"
              name="paymentDueDate"
              type="date"
              value={editableInstallment.paymentDueDate || ''}
              placeholder="Termin płatności"
              onChange={handleChange}
            />
          </div>
          <div>
            <FormLabel htmlFor="percentage">% Ceny brutto</FormLabel>
            <FormInput
              id="percentage"
              name="percentage"
              type="number"
              value={editableInstallment.percentage * 100.0}
              placeholder="% Ceny brutto"
              disabled
            />
          </div>
          <div>
            <FormLabel htmlFor="amount">Kwota</FormLabel>
            <FormInput
              id="amount"
              name="amount"
              type="number"
              value={editableInstallment.amount}
              placeholder="Kwota"
              disabled
            />
          </div>

          <div>
            <FormLabel htmlFor="payment_status">Status</FormLabel>
            <select
              id="payment_status"
              name="payment_status"
              value={editableInstallment.payment_status || ''}
              onChange={handleChange}
              className="w-full h-[42px] px-3 border border-[#D7D7D7] rounded-[6px] bg-white text-[var(--electric-blue)] focus:outline-none focus:border-[var(--electric-blue)]"
            >
              <option value="">-- Wybierz status --</option>
              {STATUSY_PLATNOSCI.map((st) => (
                <option key={st} value={st}>{st}</option>
              ))}
            </select>
          </div>
          <div>
            <FormLabel htmlFor="paymentDates">Data zapłaty</FormLabel>
            <FormInput
              id="paymentDates"
              name="paymentDates"
              value={editableInstallment.paymentDates || ''}
              placeholder="Data zapłaty"
              onChange={handleChange}
            />
          </div>
          <div>
            <FormLabel htmlFor="paidAmount">Zapłacono</FormLabel>
            <FormInput
              id="paidAmount"
              name="paidAmount"
              type="number"
              value={editableInstallment.paidAmount || ''}
              placeholder="Zapłacono"
              onChange={handleChange}
            />
          </div>
          <div>
            <FormLabel htmlFor="remainingAmount">Pozostało</FormLabel>
            <FormInput
              id="remainingAmount"
              name="remainingAmount"
              type="number"
              value={editableInstallment.remainingAmount || ''}
              placeholder="Pozostało"
              onChange={handleChange}
            />
          </div>
          <div>
            <FormLabel htmlFor="withdrawnAmount">Wypłacono</FormLabel>
            <FormInput
              id="withdrawnAmount"
              name="withdrawnAmount"
              type="number"
              value={editableInstallment.withdrawnAmount || ''}
              placeholder="Wypłacono"
              onChange={handleChange}
            />
          </div>
          <div>
            <FormLabel htmlFor="dateField">Wypłacono dnia</FormLabel>
            <FormInput
              id="dateField"
              name="dateField"
              value={editableInstallment.dateField || ''}
              placeholder="Wypłacono dnia"
              onChange={handleChange}
            />
          </div>

          {isProjectManager() && (
            <div className="col-span-2">
              <FormLabel htmlFor="changeNote">Uzasadnienie zmiany</FormLabel>
              <textarea
                id="changeNote"
                value={changeNote}
                onChange={(e) => setChangeNote(e.target.value)}
                placeholder="Opisz powód zmiany..."
                className="w-full px-3 py-2 border border-[#D7D7D7] rounded-[6px] bg-white text-[var(--electric-blue)] focus:outline-none focus:border-[var(--electric-blue)] min-h-[80px]"
              />
            </div>
          )}

          <div className="col-span-2 flex justify-end mt-10">
            <Button type="submit" variant="default" disabled={submitting}>
              {isProjectManager() ? 'Wyślij do zatwierdzenia' : 'Zapisz'}
            </Button>
            <Button type="button" variant="bordered" onClick={onClose} className="ml-4">
              Anuluj
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default EditInstallmentModal;
